import React, { useState } from "react";

/**
 * Responsive, lazy-loaded image with WebP via the /api/img server-side transformer.
 * Renders a <picture> with multi-density srcsets (480→3000 w) so retina/Mac displays
 * stay sharp. While the image is loading the wrapper holds a neutral background so
 * users never see a broken-image icon or a layout shift.
 *
 * Props:
 *  - src       : original image URL (e.g. /api/files/uzungol/x.jpg or absolute https://...)
 *  - srcMobile : optional mobile-optimised source. Rendered as <source media="(max-width: 768px)"> ahead of the main webp source.
 *  - alt       : alt text (required)
 *  - sizes     : standard `sizes` attribute (e.g. "(min-width: 1024px) 50vw, 100vw")
 *  - width     : intrinsic width hint
 *  - className, style : forwarded to <img>
 *  - eager     : if true, sets loading="eager" + fetchpriority="high"
 *  - hero      : if true, uses a wider srcset (1200/1920/2400/3000) and skips opacity fade-in
 *  - placeholderClassName : optional Tailwind class for the loading bg (default neutral)
 */
export default function OptImage({
  src, srcMobile, alt = "", sizes = "100vw", width, className = "", style,
  eager = false, hero = false, placeholderClassName = "bg-[#F5F2EC]",
  wrapperClassName = "block",
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  if (!src) return <span className={wrapperClassName + " " + placeholderClassName} style={style} />;

  const isApiFile = typeof src === "string" && src.includes("/api/files/");
  const isMobileApi = typeof srcMobile === "string" && srcMobile.includes("/api/files/");

  // Non-/api/files sources: still render <picture> if we have a srcMobile, else direct <img>.
  if (!isApiFile) {
    if (srcMobile) {
      return (
        <span className={"relative " + wrapperClassName + " " + (loaded ? "" : placeholderClassName)} style={style}>
          <picture>
            <source media="(max-width: 768px)" srcSet={srcMobile} />
            <img
              src={src}
              alt={alt}
              loading={eager ? "eager" : "lazy"}
              fetchPriority={eager ? "high" : "auto"}
              decoding="async"
              sizes={sizes}
              width={width}
              onLoad={() => setLoaded(true)}
              onError={() => setErrored(true)}
              className={className + (hero ? "" : " transition-opacity duration-300 ") + (errored ? " opacity-0" : (hero || loaded ? " opacity-100" : " opacity-0"))}
              {...rest}
            />
          </picture>
        </span>
      );
    }
    return (
      <span className={"relative " + wrapperClassName + " " + (loaded ? "" : placeholderClassName)} style={style}>
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : "auto"}
          decoding="async"
          sizes={sizes}
          width={width}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={className + (errored ? " opacity-0" : "")}
          {...rest}
        />
      </span>
    );
  }

  const enc = encodeURIComponent(src);
  const make = (u, w, fmt) => (w ? `/api/img?u=${encodeURIComponent(u)}&w=${w}&fmt=${fmt}` : `/api/img?u=${encodeURIComponent(u)}&fmt=${fmt}`);
  const widthsDesktop = hero ? [800, 1200, 1920, 2400] : [480, 800, 1200, 1600];
  const widthsMobile = hero ? [480, 800, 1200] : [320, 480, 640, 800];
  const buildSrcset = (u, fmt, widths, includeOriginal = false) => {
    const entries = widths.map((w) => `${make(u, w, fmt)} ${w}w`);
    if (includeOriginal) entries.push(`${make(u, null, fmt)} 3000w`);
    return entries.join(", ");
  };
  const webpDesktop = buildSrcset(src, "webp", widthsDesktop, hero);
  const jpegDesktop = buildSrcset(src, "jpeg", widthsDesktop, hero);
  const webpMobile = isMobileApi ? buildSrcset(srcMobile, "webp", widthsMobile, hero) : null;
  const jpegMobile = isMobileApi ? buildSrcset(srcMobile, "jpeg", widthsMobile, hero) : null;
  const fallback = hero ? make(src, 1920, "jpeg") : make(src, 1200, "jpeg");

  // For hero slides, opacity fade-in causes a visible "fade up from dark" flash on
  // every slide-switch. With preload + cross-fade in the parent slider, we can skip
  // the per-image fade entirely.
  const imgOpacity = hero ? "opacity-100" : (loaded ? "opacity-100" : "opacity-0");

  return (
    <span className={"relative " + wrapperClassName + " " + (loaded ? "" : placeholderClassName)} style={style}>
      <picture>
        {webpMobile && <source type="image/webp" media="(max-width: 768px)" srcSet={webpMobile} sizes={sizes} />}
        {jpegMobile && <source type="image/jpeg" media="(max-width: 768px)" srcSet={jpegMobile} sizes={sizes} />}
        <source type="image/webp" srcSet={webpDesktop} sizes={sizes} />
        <source type="image/jpeg" srcSet={jpegDesktop} sizes={sizes} />
        <img
          src={fallback}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : "auto"}
          decoding="async"
          className={className + (hero ? " " : " transition-opacity duration-300 ") + imgOpacity}
          width={width}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          {...rest}
        />
      </picture>
    </span>
  );
}
